import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Trash2 } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEventGroup } from '@/lib/api/types';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { dateInputToIsoStart, dateInputToIsoEnd, isoToDateInput } from './planner/dates';

type ProjectStatus = 'planning' | 'live' | 'wrapped';

interface ProjectEditFormState {
  name: string;
  projectStatus: ProjectStatus;
  startLocal: string;
  endLocal: string;
}

interface Props {
  group: XkEventGroup;
  onCancel: () => void;
  onSaved: (g: XkEventGroup) => void;
  onDeleted: () => void;
}

/**
 * Project editor, body only — hosted by the right rail (`FormPanelHost`).
 * Replaces the `EditProjectDialog` modal that used to live inside
 * `ProjectDetailPage`: selecting a project edits it in place, in context.
 */
export function ProjectEditForm({ group, onCancel, onSaved, onDeleted }: Props) {
  const queryClient = useQueryClient();
  const t = useT();
  const [form, setForm] = useState<ProjectEditFormState>(() => formFromGroup(group));
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setForm(formFromGroup(group));
    setConfirmDelete(false);
  }, [group]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.detail(group.id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.all });
    if (group.companyId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.eventGroups.byBrand(group.companyId),
      });
    }
  };

  const update = useMutation({
    mutationFn: (patch: Partial<XkEventGroup>) => eventsApi.updateGroup(group.id, patch),
    onSuccess: (updated) => {
      invalidate();
      toast({ tone: 'success', title: t('project.updatedToast', { name: updated.name }) });
      onSaved(updated);
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('project.updateFailedToast'), description: e.message }),
  });

  const remove = useMutation({
    mutationFn: () => eventsApi.deleteGroup(group.id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('project.deletedToast', { name: group.name }) });
      onDeleted();
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('project.deleteFailedToast'), description: e.message }),
  });

  const pending = update.isPending || remove.isPending;
  const canSubmit = form.name.trim().length >= 2 && !pending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const startIso = dateInputToIsoStart(form.startLocal);
    const endIso = dateInputToIsoEnd(form.endLocal);
    if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast({ tone: 'error', title: t('project.edit.rangeInvalid') });
      return;
    }
    update.mutate({
      name: form.name.trim(),
      projectStatus: form.projectStatus,
      startDate: startIso,
      endDate: endIso,
    });
  };

  const setField = <K extends keyof ProjectEditFormState>(k: K, v: ProjectEditFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const inputCls =
    'w-full rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2.5 text-sm text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none';
  const labelCls =
    'mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]';

  return (
    <form noValidate onSubmit={submit} className="flex flex-1 min-h-0 flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <label className="block">
          <div className={labelCls}>{t('project.edit.field.name')}</div>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            className={inputCls}
          />
        </label>

        <label className="block">
          <div className={labelCls}>{t('project.edit.field.status')}</div>
          <select
            value={form.projectStatus}
            onChange={(e) => setField('projectStatus', e.target.value as ProjectStatus)}
            className={inputCls}
          >
            <option value="planning">{t('project.status.planning')}</option>
            <option value="live">{t('project.status.live')}</option>
            <option value="wrapped">{t('project.status.wrapped')}</option>
          </select>
        </label>

        <label className="block">
          <div className={`${labelCls} flex items-center gap-1`}>
            <CalendarClock className="h-3 w-3" />
            {t('project.edit.field.startDate')}
          </div>
          <DatePicker
            mode="date"
            value={form.startLocal}
            onChange={(v) => setField('startLocal', v)}
            max={form.endLocal || undefined}
          />
        </label>
        <label className="block">
          <div className={`${labelCls} flex items-center gap-1`}>
            <CalendarClock className="h-3 w-3" />
            {t('project.edit.field.endDate')}
          </div>
          <DatePicker
            mode="date"
            value={form.endLocal}
            onChange={(v) => setField('endLocal', v)}
            min={form.startLocal || undefined}
          />
        </label>

        <p className="rounded-lg border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2.5 text-[11px] text-[var(--color-fg-muted)]">
          <strong className="text-[var(--color-fg-primary)]">
            {t('project.edit.rangeNoteLabel')}
          </strong>{' '}
          {t('project.edit.rangeNote')}
        </p>

        {confirmDelete && (
          <div className="rounded-lg border border-[var(--color-error)] bg-[var(--color-error)]/10 p-3 text-xs text-[var(--color-fg-primary)]">
            <strong className="text-[var(--color-error)]">
              {t('project.edit.confirmTitle')}
            </strong>{' '}
            {t('project.edit.confirmBody')}
          </div>
        )}
      </div>

      {/*
        El pie ENVUELVE. Es la única defensa real contra que un botón se
        pierda: los botones llevan `whitespace-nowrap`, el carril está anclado
        a la derecha y sin `flex-wrap` el último —«Guardar cambios»— se salía
        por el borde de la pantalla al estrechar el panel. El tope de ancho
        mínimo ayuda, pero no basta: basta una traducción más larga.

        `mr-auto` en la acción destructiva sustituye al separador `flex-1` que
        había: hace lo mismo cuando los tres caben en una línea, y al envolver
        deja el borrado arriba a la izquierda con cancelar/guardar debajo, en
        vez de convertirse en un hueco elástico que empuja.
      */}
      <div className="flex flex-none flex-wrap items-center justify-end gap-2 border-t border-[var(--color-border-subtle)] p-3">
        <Button
          type="button"
          variant="ghost"
          className="mr-auto"
          onClick={() => (confirmDelete ? remove.mutate() : setConfirmDelete(true))}
          loading={remove.isPending}
          leftIcon={<Trash2 className="h-4 w-4" />}
        >
          {confirmDelete ? t('project.edit.confirmDelete') : t('project.edit.deleteProject')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('project.edit.cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit} loading={update.isPending}>
          {t('project.edit.saveChanges')}
        </Button>
      </div>
    </form>
  );
}

function formFromGroup(g: XkEventGroup): ProjectEditFormState {
  return {
    name: g.name ?? '',
    projectStatus: (g.projectStatus as ProjectStatus) ?? 'planning',
    startLocal: isoToDateInput(g.startDate),
    endLocal: isoToDateInput(g.endDate),
  };
}
